/**
 * Живые события CRM (SSE): один поток на приложение, сколько бы экранов его ни слушали.
 *
 * Как устроено у CRM (GiperCRM/server/src/routes/events.ts):
 *   POST /api/crm/events/ticket (Bearer) → { ticket } — пропуск на 60 с, ОДНОРАЗОВЫЙ;
 *   GET  /api/crm/events?ticket=…        → поток: первое сообщение без имени {type:'connected'},
 *                                          дальше именованные события «event: deal.moved» и т.п.
 * CRM сама отсеивает события, которые человеку не положено видеть (область видимости ADR-011).
 *
 * Именованные события EventSource отдаёт только тем, кто подписан на ИМЯ, поэтому подписчик
 * называет нужные виды; поток добавляет слушателей на лету.
 *
 * Обрыв: поток закрываем и переподключаемся с паузой (1 → 2 → 5 → 10 → 30 с) за НОВЫМ пропуском
 * (старый уже использован). После переподключения подписчики получают {type:'resync'}: события,
 * пришедшие в разрыве, потеряны — экран перечитывает данные. Сюда же — возврат приложения на экран.
 *
 * Пригодится и «Звонкам», и «Календарю»: виды call.* и task.* слушаются тем же потоком.
 */

export interface CrmEvent {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
}

export type CrmEventHandler = (e: CrmEvent) => void;

/** События сделок, которые рассылает CRM (lib/eventBus.ts). */
export const DEAL_EVENT_TYPES = ['deal.created', 'deal.updated', 'deal.moved', 'deal.deleted', 'deal.changed'] as const;

/** События звонков и записей на встречу (Звонки, Календарь). */
export const CALL_EVENT_TYPES = ['call.ringing', 'call.finished', 'call.changed', 'call.decision', 'booking.created'] as const;

/** Служебное событие: поток восстановлен после обрыва — перечитайте данные. */
export const RESYNC = 'resync';

export const BACKOFF_MS = [1000, 2000, 5000, 10_000, 30_000];
export function backoffDelay(attempt: number): number {
  return BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
}

/** Минимум EventSource, который нужен потоку (в проверках — подделка). */
export interface EventSourceLike {
  addEventListener(type: string, fn: (e: MessageEvent) => void): void;
  close(): void;
  onerror: ((e: Event) => void) | null;
  onmessage: ((e: MessageEvent) => void) | null;
}

export interface CrmEventsDeps {
  ticket: () => Promise<string>;
  url: (ticket: string) => string;
  open: (url: string) => EventSourceLike;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

export type StreamState = 'idle' | 'connecting' | 'open' | 'waiting';

interface Sub { types: Set<string>; handler: CrmEventHandler }

export class CrmEventStream {
  private subs = new Set<Sub>();
  private es: EventSourceLike | null = null;
  private listening = new Set<string>();
  private attempt = 0;
  private timer: unknown = null;
  private everOpened = false;
  /** Номер подключения: ответ на устаревший запрос пропуска не открывает поток. */
  private gen = 0;
  state: StreamState = 'idle';

  constructor(private deps: CrmEventsDeps) {}

  /** Подписаться на виды событий. Возвращает отписку; последний ушедший закрывает поток. */
  subscribe(types: readonly string[], handler: CrmEventHandler): () => void {
    const sub: Sub = { types: new Set(types), handler };
    this.subs.add(sub);
    if (this.es) for (const t of types) this.listen(t);
    if (this.state === 'idle') void this.connect();
    return () => {
      this.subs.delete(sub);
      if (this.subs.size === 0) this.stop();
    };
  }

  /** Переподключиться сейчас (вернулись в приложение, сменили проект). */
  reconnectNow(): void {
    if (this.subs.size === 0) return;
    this.closeSource();
    this.attempt = 0;
    void this.connect();
  }

  stop(): void {
    this.gen++;
    this.closeSource();
    this.everOpened = false;
    this.attempt = 0;
    this.state = 'idle';
  }

  private closeSource() {
    if (this.timer !== null) (this.deps.clearTimer ?? clearTimeout)(this.timer as ReturnType<typeof setTimeout>);
    this.timer = null;
    this.es?.close();
    this.es = null;
    this.listening.clear();
  }

  private async connect(): Promise<void> {
    const my = ++this.gen;
    this.state = 'connecting';
    let ticket: string;
    try {
      ticket = await this.deps.ticket();
    } catch {
      if (my === this.gen) this.retry();
      return;
    }
    if (my !== this.gen || this.subs.size === 0) return;
    const es = this.deps.open(this.deps.url(ticket));
    this.es = es;
    es.onmessage = (e) => {
      // Первое сообщение без имени — «подключено».
      try {
        if ((JSON.parse(String(e.data)) as { type?: string }).type !== 'connected') return;
      } catch { return; }
      this.state = 'open';
      this.attempt = 0;
      if (this.everOpened) this.emit({ type: RESYNC, payload: null });
      this.everOpened = true;
    };
    es.onerror = () => {
      if (my !== this.gen) return;
      this.closeSource();
      this.retry();
    };
    for (const s of this.subs) for (const t of s.types) this.listen(t);
  }

  private retry() {
    if (this.subs.size === 0) { this.state = 'idle'; return; }
    this.state = 'waiting';
    const delay = backoffDelay(this.attempt++);
    this.timer = (this.deps.setTimer ?? setTimeout)(() => { this.timer = null; void this.connect(); }, delay);
  }

  private listen(type: string) {
    if (!this.es || this.listening.has(type) || type === RESYNC) return;
    this.listening.add(type);
    this.es.addEventListener(type, (e: MessageEvent) => {
      let payload: unknown = null;
      try { payload = JSON.parse(String(e.data)); } catch { /* событие без тела — тоже повод */ }
      this.emit({ type, payload });
    });
  }

  private emit(ev: CrmEvent) {
    for (const s of [...this.subs]) {
      if (ev.type !== RESYNC && !s.types.has(ev.type)) continue;
      try { s.handler(ev); } catch (err) { console.warn('[события CRM] обработчик упал:', err); }
    }
  }
}
